import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { enablePush, pushSupported } from "../lib/push";

const AUTO_PROMPT_KEY = "qdn_push_auto_prompted";
const LAST_SEEN_KEY = "qdn_notif_last_seen";

export function PushBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  // Auto native permission prompt ~1.8s after load / right after login, then subscribe.
  useEffect(() => {
    if (!pushSupported()) return undefined;
    const timer = setTimeout(async () => {
      try {
        if (Notification.permission === "denied") return;
        if (Notification.permission === "default") {
          if (localStorage.getItem(AUTO_PROMPT_KEY)) return;
          localStorage.setItem(AUTO_PROMPT_KEY, "1");
          const result = await Notification.requestPermission();
          if (result !== "granted") return;
        }
        if (user) await enablePush();
      } catch {
        /* silently ignore auto-prompt failures */
      }
    }, 1800);
    return () => clearTimeout(timer);
  }, [user]);

  // Unread indicator from admin broadcasts vs last-seen timestamp.
  useEffect(() => {
    let active = true;
    const load = () => {
      api
        .get("/notifications")
        .then((r) => {
          if (!active) return;
          const lastSeen = localStorage.getItem(LAST_SEEN_KEY) || "1970-01-01T00:00:00Z";
          const list = r.data.notifications || [];
          setUnread(list.filter((n) => (n.at || "") > lastSeen).length);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [user]);

  return (
    <button
      type="button"
      onClick={() => navigate("/notifications")}
      data-testid="notification-bell-button"
      aria-label="Notifications"
      title="Notifications"
      className="relative grid h-10 w-10 shrink-0 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors hover:border-[#FF5200] hover:text-[#FF5200] active:scale-95"
    >
      <Bell size={18} strokeWidth={2} />
      {unread > 0 && (
        <span
          data-testid="notification-unread-dot"
          className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#FF5200] ring-2 ring-white"
        />
      )}
    </button>
  );
}
